// src/components/ActiveSwarmCard/EventInfo.js

import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { eventDateGridFormatter, eventDatesFormatter, toStdDate } from '../../../../../../utils/dates';

// Define a smaller component for displaying the event link and tooltip
const EventLink = ({ event, primaryEventUrl }) => (
  <Tooltip title={event.name} placement="top">
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: '4px',
      }}
    >
      <div
        style={{
          color: primaryEventUrl ? '#1976D2' : 'inherit',
          textDecoration: primaryEventUrl ? 'underline' : 'none',
          fontWeight: '600',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}
      >
        {primaryEventUrl ? (
          <a
            href={`${primaryEventUrl}`}
            target="_blank" rel="noreferrer"
            rel="noopener noreferrer"
            style={{
              color: '#1976D2',
              textDecoration: 'none',
            }}
          >
            {event.name}
          </a>
        ) : (
          <span>{event.name}</span>
        )}
      </div>
      {primaryEventUrl && (
        <a
          href={`${primaryEventUrl}`}
          target="_blank" rel="noreferrer"
          rel="noopener noreferrer"
          style={{
            color: '#1976D2',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <OpenInNewOutlinedIcon color='primary' style={{ height: "18px" }} />
        </a>
      )}
    </div>
  </Tooltip>
);

/**
 * EventInfo Component
 * Displays event details including name with link, sales date, swarm code, and location.
 * 
 * @param {Object} props
 * @param {Object} props.event - Event data.
 * @param {string} props.eventDate - eventDate
 * @param {string} props.code - Swarm code to display and copy.
 * @param {Function} props.onCopy - Function to handle copying the code.
 * @returns {JSX.Element}
 */
const EventInfo = ({ event, eventDate, code, onCopy, primaryEventUrl }) => (
  <div style={{ width: '100%', maxWidth: '210px' }}> {/* Restamos el padding de la card */}
    {/* Event Name with External Link */}
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      {event && (
        <EventLink event={event} primaryEventUrl={primaryEventUrl} />
      )}
    </div>

    {/* Sales Date and Swarm Code */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {/* Sales Date */}
      {eventDateGridFormatter(eventDate)}
    </div>

    {/* Event Location */}
    {event && (
      <Tooltip title={`${event.venue.name}, ${event.venue.city || 'N/A'}`} placement="bottom">
        <div style={{ 
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%'
        }}>
          {`${event.venue.name}, ${event.venue.city || 'N/A'}`}
        </div>
      </Tooltip>
    )}
  </div>
);

EventInfo.propTypes = {
  event: PropTypes.shape({
    bteId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    venue: PropTypes.shape({
      name: PropTypes.string,  // Made optional since we handle null with || "N/A"
      city: PropTypes.string,  // Already optional
    }).isRequired,
  }).isRequired,
  eventDate: PropTypes.string.isRequired,  // Added missing prop
  code: PropTypes.string.isRequired,
  onCopy: PropTypes.func.isRequired,
  primaryEventUrl: PropTypes.string,  // Made optional
};

EventInfo.defaultProps = {
  event: {
    venue: {
      name: 'N/A',
      city: 'N/A'
    }
  }
};

export default EventInfo;
